'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTheme } from 'next-themes'
import { cn } from '@/lib/utils'
import { ChromaText } from '@/components/ui/chroma-text'
import { ScrollRow } from '@/components/ui/scroll-row'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { useMounted } from '@/lib/opendocs/hooks/use-mounted'

type Stat = { value: string; label: string }
type Cta = { type: 'primary' | 'link'; label: string; href: string }
type Case = {
  id: string
  logoSrc: string
  logoAlt: string
  /** Intrinsic pixels of the logo file, so every <img> can declare
      width/height and the browser reserves the box before the file lands. */
  logoW: number
  logoH: number
  industry: string
  quote: string
  stats: Stat[]
  ctas: Cta[]
}

const CASES: Case[] = [
  {
    id: 'dypcet',
    logoSrc: '/stories/dyp.webp',
    logoW: 692,
    logoH: 220,
    logoAlt: 'DY Patil College of Engineering & Technology',
    industry: 'Engineering Education',
    quote:
      'DYPCET needed more than a website, they needed a complete digital command center. IntelQI delivered a modern, responsive institutional website paired with a powerful admin panel that put content control directly into their team’s hands. No more waiting on developers for simple updates. In addition, a separate AI Interview Portal streamlines the candidate evaluation process, making it faster, fairer, and far less labor-intensive than before.',
    stats: [
      { value: '100%', label: 'internal content management independence' },
      { value: '60%', label: 'reduction in interview coordination effort' },
      // { value: "24/7", label: "real-time website content control" },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/dypcet',
      },
    ],
  },
  {
    id: 'dyptc-talsande',
    logoSrc: '/stories/dyptc1.webp',
    logoW: 700,
    logoH: 216,
    logoAlt: 'D. Y. Patil Technical Campus, Talsande',
    industry: 'Multi-Program Education Campus',
    quote:
      "Imagine running three distinct academic programs under one roof each with its own coordinator, its own workflows, and its own administrative needs. That was DYPTC's reality. IntelQI built a unified digital platform that serves as the single source of truth for the entire campus, while giving each program its own independent admin panel with role-based access. Centralized control where it matters. Local flexibility where it's needed.",
    stats: [
      { value: '3', label: 'programs managed under one unified platform' },
      { value: '100%', label: 'role-based access clarity across teams' },
      // { value: "0", label: "technical dependencies for daily updates" },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/dyptc',
      },
    ],
  },
  {
    id: 'walchand',
    logoSrc: '/stories/walchandlog.webp',
    logoW: 700,
    logoH: 235,
    logoAlt: 'Walchand College of Engineering',
    industry: 'Engineering Education',
    quote:
      'When Walchand College needed to shift from traditional classrooms to a robust digital learning environment, they chose IntelQI to build the foundation. A fully customized Learning Management System now houses every course, assignment, and assessment. The integrated Online Examination System handles thousands of students simultaneously with security, reliability, and automated evaluation workflows. Faculty teach. Students learn. IntelQI handles the rest.',
    stats: [
      { value: '2000+', label: 'students supported in online exams' },
      // { value: "70%", label: "reduction in exam administration overhead" },
      { value: '24/7', label: 'learning resource accessibility' },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/walchand',
      },
    ],
  },
  {
    id: 'kjsomaiya',
    logoSrc: '/stories/kjsomaiya.webp',
    logoW: 385,
    logoH: 113,
    logoAlt: 'K. J. Somaiya Institute',
    industry: 'Higher Education',
    quote:
      'Processing thousands of candidate interviews each admission cycle was overwhelming until K. J. Somaiya Institute deployed the IntelQI AI Interview Portal. What once required endless coordination spreadsheets and fragmented feedback is now a structured, transparent, and remarkably fast digital workflow. Evaluators apply consistent criteria, candidates experience fairness, and the administrative team has reclaimed hours once lost to scheduling chaos.',
    stats: [
      { value: '70%', label: 'faster interview coordination' },
      { value: '100%', label: 'centralized candidate evaluation records' },
      // { value: "3+", label: "admission cycles successfully streamlined" },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/kj-somaiya',
      },
    ],
  },
  {
    id: 'stjohn-interview',
    logoSrc: '/stories/stjohn.webp',
    logoW: 621,
    logoH: 169,
    logoAlt: 'St. John College Interview Portal',
    industry: 'Healthcare & General Education',
    quote:
      "High-volume candidate interviews were bottlenecking St. John's admissions process. The IntelQI St. John Interview Portal changed everything. Structured, AI-assisted interviews replaced inconsistent manual evaluations. Centralized feedback eliminated lost notes and email threads. And the administrative team? They stopped chasing schedules and started focusing on what matters finding the right candidates. Built specifically for St. John's unique requirements, this portal delivers fairness, speed, and complete transparency.",
    stats: [
      { value: '75%', label: 'faster interview-to-decision turnaround' },
      {
        value: '100%',
        label: 'consistent evaluation criteria across all candidates',
      },
      // { value: "0", label: "lost or missing feedback records" },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/st-john-baptist-college',
      },
    ],
  },
  {
    id: 'jskb-associates',
    logoSrc: '/stories/jskba.webp',
    logoW: 1920,
    logoH: 634,
    logoAlt: 'JSKB Associates',
    industry: 'Financial Services',
    quote:
      "Spreadsheets, email chains, and manual reconciliations defined JSKB's financial operations until IntelQI stepped in. The custom Finance Module transformed chaos into clarity. Automated ledgers, real-time payment tracking, digital receipts, and structured reporting now power every transaction. Month-end closings that once took days are completed in hours. Compliance audits that required weeks of preparation now run smoothly with export-ready records. This isn't just automation it's financial peace of mind.",
    stats: [
      { value: '65%', label: 'reduction in financial processing time' },
      { value: '100%', label: 'audit-ready financial records at all times' },
      // { value: "Real-time", label: "visibility into all transactions" },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/jskb-associates',
      },
    ],
  },
  {
    id: 'pillai-university',
    logoSrc: '/stories/pillai.webp',
    logoW: 700,
    logoH: 330,
    logoAlt: 'Pillai University',
    industry: 'University',
    quote:
      'Pillai University needed a single platform to run four completely separate entrance examinations on the same day, each with its own candidate pool, its own question structure, and its own timing window. IntelQI deployed the Online Examination Portal for Pillai Pulse 2026, configuring independent test environments for MBA-PGDM, BTech, BBA-BCA, and BDES simultaneously. Every session ran without overlap. Results were processed automatically the moment each window closed. No manual evaluation, no coordination chaos.',
    stats: [
      { value: '4', label: 'programs examined simultaneously on a single day' },
      { value: '100%', label: 'automated evaluation with zero manual grading' },
    ],
    ctas: [
      {
        type: 'primary',
        label: 'Read their story',
        href: '/customer-stories/pillai-university',
      },
    ],
  },
]

export default function CustomerStoriesSection() {
  const { theme, resolvedTheme } = useTheme()
  const mounted = useMounted()

  const [isDark, setIsDark] = useState<boolean>(false)
  const [selected, setSelected] = useState<number>(0)

  const btnRefs = useRef<Array<HTMLButtonElement | null>>([])
  const tabListRef = useRef<HTMLDivElement | null>(null)
  const ids = CASES.map((_, i) => `case-tab-${i}`)

  useEffect(() => {
    if (!mounted) return
    const id = setTimeout(() => {
      setIsDark((resolvedTheme ?? theme ?? 'dark') === 'dark')
    }, 0)
    return () => clearTimeout(id)
  }, [theme, resolvedTheme, mounted])

  const effectiveIsDark = mounted ? isDark : false

  // Centre the active tab in its own scroller. Scroll the list, never the tab:
  // scrollIntoView walks every scrollable ancestor, so on mount it dragged the
  // whole page down to this section. Skip the first run for the same reason.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    const btn = btnRefs.current[selected]
    const list = tabListRef.current
    if (!btn || !list || list.scrollWidth <= list.clientWidth) return

    const left = btn.offsetLeft - (list.clientWidth - btn.offsetWidth) / 2
    list.scrollTo({
      left: Math.max(0, Math.min(left, list.scrollWidth - list.clientWidth)),
      behavior: 'smooth',
    })
  }, [selected])

  const handleSelect = useCallback((idx: number) => {
    setSelected(idx)
  }, [])
  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const map: Record<string, number> = {
      ArrowRight: (selected + 1) % CASES.length,
      ArrowLeft: (selected - 1 + CASES.length) % CASES.length,
      Home: 0,
      End: CASES.length - 1,
    }

    if (!(e.key in map)) return

    e.preventDefault()

    const next = map[e.key as keyof typeof map]

    if (next === undefined) return

    btnRefs.current[next]?.focus()
    handleSelect(next)
  }

  return (
    <section className="w-full py-16 sm:py-20 md:py-28 bg-transparent">
      <div className="container mx-auto">
        {/* ── Section header ──────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2
            className={cn(
              'font-heading text-2xl sm:text-3xl md:text-5xl font-normal leading-tight',
              'text-foreground'
            )}
            suppressHydrationWarning
          >
            <ChromaText>Trusted by leading organisations worldwide</ChromaText>
          </h2>

          <div className="md:text-right md:shrink-0 md:pt-1">
            <a
              href="/customer-stories"
              aria-label="View all customer stories"
              className={cn(
                'inline-flex items-center gap-2 text-sm sm:text-base md:text-lg font-medium group transition-colors',
                'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
                'text-foreground focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
              )}
            >
              <span className="relative inline-flex items-center gap-1.5">
                <span className="relative">
                  View all customer stories
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-600 dark:bg-primary-active transition-all duration-300 group-hover:w-full" />
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                  className="h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300 group-hover:translate-x-1"
                >
                  <path
                    d="M6 4l8 8-8 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </a>
          </div>
        </div>

        {/* ── Logo tab bar + content ───────────────────────────────────────── */}
        <div className="mt-10 sm:mt-12">
          <ScrollRow
            scrollerRef={tabListRef}
            role="tablist"
            aria-label="Customer stories"
            wrapperClassName="w-full"
            className={cn(
              'w-full pt-4 sm:pt-6 md:pt-8',
              // Mobile: scrollable flex row (mask + next button via ScrollRow)
              'flex gap-0 scroll-smooth snap-x snap-mandatory',
              // Tablet+: 6-col grid, overflow hidden so nothing spills
              'sm:grid sm:grid-cols-7 sm:overflow-visible'
            )}
          >
            {CASES.map((c, idx) => {
              const isSelected = selected === idx
              return (
                <button
                  key={c.id}
                  id={ids[idx]}
                  type="button"
                  role="tab"
                  ref={(el) => {
                    btnRefs.current[idx] = el
                  }}
                  onClick={() => handleSelect(idx)}
                  onKeyDown={handleKey}
                  aria-selected={isSelected}
                  aria-controls={`case-panel-${idx}`}
                  tabIndex={isSelected ? 0 : -1}
                  title={c.logoAlt}
                  className={cn(
                    'group flex flex-col items-center gap-3 bg-transparent focus:outline-none',
                    'snap-center',
                    // Mobile fixed-width cell
                    'min-w-[88px] w-[88px] shrink-0',
                    // Tablet+: let grid column dictate width
                    'sm:w-auto sm:min-w-0 sm:shrink',
                    // Padding inside each cell
                    'px-2 sm:px-2 md:px-3 lg:px-4',
                    'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2',
                    'focus-visible:outline-emerald-600 dark:focus-visible:outline-primary-active'
                  )}
                >
                  <img
                    src={c.logoSrc}
                    alt={c.logoAlt}
                    width={c.logoW}
                    height={c.logoH}
                    loading="lazy"
                    decoding="async"
                    className={cn(
                      'w-full object-contain transition-all duration-200 dark:grayscale dark:invert',
                      // Larger heights clearly visible on every screen
                      'h-11 sm:h-12 md:h-14 lg:h-16',
                      isSelected
                        ? 'opacity-100'
                        : 'grayscale opacity-75 group-hover:opacity-100 group-hover:grayscale-0 dark:group-hover:grayscale'
                    )}
                  />
                  {/* Active-indicator bar */}
                  <span
                    role="presentation"
                    style={{ height: '2.5px' }}
                    className={cn(
                      'block w-full rounded-full transition-colors duration-200',
                      isSelected ? 'bg-foreground' : 'bg-foreground/15'
                    )}
                  />
                </button>
              )
            })}
          </ScrollRow>

          {/* Mobile dot pagination */}
          <div
            className="flex sm:hidden items-center justify-center gap-2 mt-4"
            aria-hidden="true"
          >
            {CASES.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelect(idx)}
                aria-label={`Go to ${item.logoAlt}`}
                className={cn(
                  'rounded-full transition-all duration-200 focus:outline-none',
                  selected === idx
                    ? 'w-4 h-2 bg-foreground'
                    : 'w-2 h-2 bg-foreground/15'
                )}
              />
            ))}
          </div>

          {/* ── Case Study Panels ────────────────────────────────────────
              All panels are stacked in a single CSS-grid cell so the wrapper
              is always as tall as the TALLEST story. Switching tabs only
              crossfades opacity — the height never changes, so there is zero
              layout shift regardless of quote length. */}
          <div className="grid pt-10 sm:pt-12 md:pt-14 lg:pt-20">
            {/* Decorative brace — ONE static layer behind the crossfading
                panels, so switching tabs never re-fades or restarts it */}
            <div
              aria-hidden="true"
              className="[grid-area:1/1] pointer-events-none hidden xl:grid grid-cols-12 items-center gap-20"
            >
              <div className="col-span-4 relative">
                <div className="absolute -right-12 top-1/2 -translate-y-1/2 flex w-24 items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="111"
                    height="392"
                    viewBox="0 0 111 392"
                    fill="none"
                  >
                    <defs>
                      <linearGradient
                        id="brace-green"
                        x1="55.5"
                        y1="0"
                        x2="55.5"
                        y2="392"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop className="[stop-color:#059669] dark:[stop-color:hsl(var(--primary-active))]" />
                        <stop
                          offset="1"
                          className="[stop-color:#059669] dark:[stop-color:hsl(var(--primary-active))]"
                        />
                      </linearGradient>
                    </defs>
                    {/* Base: the original light gray */}
                    <path
                      d="M110.976 391.68V357H91.392C72.216 357 67.32 349.248 67.32 329.664V239.904C67.32 215.016 59.568 202.368 43.656 195.84C59.568 189.72 67.32 177.48 67.32 152.592V62.016C67.32 42.84 72.216 34.68 91.392 34.68H110.976V0H82.008C44.472 0 28.152 21.216 28.152 58.344V152.592C28.152 168.504 23.664 178.704 5.304 178.704H0V213.384H5.304C23.664 213.384 28.152 223.584 28.152 239.904V333.336C28.152 370.464 44.472 391.68 82.008 391.68H110.976Z"
                      className="fill-foreground/10"
                    />
                    {/* Faint green gradient breathing over the gray */}
                    <path
                      className="brace-pulse"
                      d="M110.976 391.68V357H91.392C72.216 357 67.32 349.248 67.32 329.664V239.904C67.32 215.016 59.568 202.368 43.656 195.84C59.568 189.72 67.32 177.48 67.32 152.592V62.016C67.32 42.84 72.216 34.68 91.392 34.68H110.976V0H82.008C44.472 0 28.152 21.216 28.152 58.344V152.592C28.152 168.504 23.664 178.704 5.304 178.704H0V213.384H5.304C23.664 213.384 28.152 223.584 28.152 239.904V333.336C28.152 370.464 44.472 391.68 82.008 391.68H110.976Z"
                      fill="url(#brace-green)"
                    />
                  </svg>
                </div>
              </div>
              <div className="col-span-8" />
            </div>
            {CASES.map((c, idx) => {
              const isActive = idx === selected
              return (
                <div
                  key={c.id}
                  id={`case-panel-${idx}`}
                  role="tabpanel"
                  aria-labelledby={ids[idx]}
                  aria-hidden={!isActive}
                  className={cn(
                    // Items stretch (no items-center): every panel's columns
                    // fill the full stacked height, so the tag/quote anchor to
                    // the same top line and the CTA pins to the same bottom
                    // line on every tab — nothing drifts with quote length.
                    '[grid-area:1/1] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-20',
                    'transition-opacity duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                    isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  )}
                >
                  {/* ── Left: selected logo + stats ──────────────────────────── */}
                  <aside className="lg:col-span-4 flex flex-col justify-center items-start">
                    {/* ── MOBILE: side-by-side logo + stats ── */}
                    <div className="flex sm:hidden w-full items-center gap-4">
                      {/* Logo left column */}
                      <div className="flex items-center justify-center w-[45%] shrink-0">
                        <img
                          src={c.logoSrc}
                          alt={c.logoAlt}
                          width={c.logoW}
                          height={c.logoH}
                          loading="lazy"
                          decoding="async"
                          className="object-contain opacity-90 w-full h-auto max-h-[100px] dark:grayscale dark:invert"
                        />
                      </div>

                      {/* Stats right column, stacked */}
                      <ul className="flex flex-col gap-4 w-[55%]">
                        {c.stats.map((s, i) => (
                          <li key={i}>
                            <p className="text-3xl font-semibold text-emerald-600 dark:text-primary-active leading-none">
                              <AnimatedNumber value={s.value} delay={i * 70} />
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                              {s.label}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* ── TABLET + DESKTOP: logo above, then stats ── */}
                    <div className="hidden sm:flex flex-col w-full">
                      {/* Logo */}
                      <div className="mb-5 md:mb-6">
                        <img
                          src={c.logoSrc}
                          alt={c.logoAlt}
                          width={c.logoW}
                          height={c.logoH}
                          loading="lazy"
                          decoding="async"
                          className={cn(
                            'object-contain opacity-90 transition-all duration-200 dark:grayscale dark:invert',
                            'h-14 md:h-16 xl:h-[72px]',
                            'w-auto max-w-[220px] md:max-w-[260px] lg:max-w-[280px]'
                          )}
                        />
                      </div>

                      {/* sm–md: stats side-by-side; xl: stacked
                          (the decorative brace lives in the static overlay
                          layer above, not per-panel) */}
                      <ul className="flex flex-row gap-6 md:gap-8 xl:flex-col xl:gap-0">
                        {c.stats.map((s, i) => (
                          <li key={i} className="xl:mt-8 first:xl:mt-0">
                            <p className="text-4xl md:text-5xl font-semibold text-emerald-600 dark:text-primary-active">
                              <AnimatedNumber value={s.value} delay={i * 70} />
                            </p>
                            <p className="mt-1 text-sm leading-relaxed max-w-[200px] text-muted-foreground">
                              {s.label}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </aside>

                  {/* ── Right: industry tag + quote + CTA ────────────────────── */}
                  <article className="lg:col-span-8 flex flex-col lg:pl-8">
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                      {c.industry}
                    </p>

                    {/* Left-aligned rag on a book-width measure — justified
                        text at this size opens rivers of whitespace. */}
                    <blockquote
                      className={cn(
                        'mt-4 max-w-[64ch] text-pretty text-foreground/90',
                        'text-[15px]/7 sm:text-base/7 md:text-[17px]/8 lg:text-lg/8 xl:text-xl/9',
                        'tracking-[-0.01em]'
                      )}
                    >
                      "{c.quote}"
                    </blockquote>

                    {/* mt-auto pins the CTA to the panel's shared bottom line
                        (no-op on mobile, where the panel is content-sized) */}
                    <div className="mt-auto pt-6 sm:pt-8">
                      <a
                        href={c.ctas?.[0]?.href}
                        tabIndex={isActive ? 0 : -1}
                        className={cn(
                          'inline-flex items-center gap-2 rounded-md',
                          'px-4 py-2.5 sm:px-5 sm:py-3',
                          'text-sm sm:text-base font-medium shadow-sm',
                          'transition-all duration-200 group',
                          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                          'focus-visible:ring-ring'
                        )}
                        aria-label={c.ctas?.[0]?.label}
                      >
                        {c.ctas?.[0]?.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M6 4l8 8-8 8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    </div>
                  </article>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Hide webkit scrollbar on the logo tab row */}
      <style jsx global>{`
        [role='tablist']::-webkit-scrollbar {
          display: none;
        }

        /* Decorative brace: faint green gradient slowly breathing over the
           gray base */
        @keyframes brace-pulse {
          0%,
          100% {
            opacity: 0.08;
          }
          50% {
            opacity: 0.4;
          }
        }
        .brace-pulse {
          animation: brace-pulse 5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .brace-pulse {
            animation: none;
            opacity: 0.2;
          }
        }
      `}</style>
    </section>
  )
}
